import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Loader2, Pencil, RefreshCw, ShieldAlert, UserPlus, X } from 'lucide-react';
import CopyButton from '../components/dietitian/CopyButton';
import DietitianSettingsForm from '../components/dietitian/DietitianSettingsForm';
import { dietitianStrings } from '../components/dietitian/strings';
import { btnGhost, btnPrimary, btnSecondary, card, errorText, hint, input, label, pageTitle } from '../components/dietitian/ui';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { makeT, useLanguage } from '../hooks/useLanguage';
import { DietitianSettings, PricingPresets, Team } from '../services/dietitianApi';
import { Companies, RegistrationInvites } from '../services/employeeApi';
import { presetName, staffSignupLink } from '../utils/dietitianInvite';

const strings = {
  english: {
    ...dietitianStrings.english,
    'page.title': 'Team',
    'page.subtitle': "Each coach has their own code and link. Clients who join through it are assigned to that coach.",
    'access.title': 'Managers only',
    'access.body': 'Your own link and settings are on My link.',
    'access.cta': 'Open My link',
    company: 'Company',
    companyOwn: 'My company',
    refresh: 'Refresh',
    'col.name': 'Name',
    'col.role': 'Role',
    'col.code': 'Code',
    'col.plan': 'Plan',
    'col.clients': 'Clients',
    'col.actions': 'Actions',
    'role.employee': 'Coach',
    'role.company_manager': 'Manager',
    'role.sys_admin': 'System admin',
    placeholder: 'Placeholder — not a real login',
    noCode: 'No code yet',
    planCompany: 'Company default',
    edit: 'Edit',
    copyLink: 'Copy link',
    empty: 'No coaches in this company yet. Invite the first one.',
    noCompany: 'This account has no company. Pick one above.',
    loadTeamError: 'Could not load the team.',
    'invite.open': 'Invite coach',
    'invite.title': 'Invite a coach',
    'invite.body': 'They get a signup link. After signing up they appear here with their own code.',
    'invite.email': 'Coach email',
    'invite.send': 'Create invite',
    'invite.creating': 'Creating…',
    'invite.done': 'Invite created. Send this signup link to {email}:',
    'invite.another': 'Invite another',
    'invite.emailError': 'Enter a valid email.',
    'invite.error': 'Could not create the invite: {msg}',
    close: 'Close',
    'panel.title': 'Edit {name}',
  },
  hebrew: {
    ...dietitianStrings.hebrew,
    'page.title': 'צוות',
    'page.subtitle': 'לכל מאמן יש קוד וקישור משלו. לקוחות שמצטרפים דרכו משויכים למאמן הזה.',
    'access.title': 'למנהלים בלבד',
    'access.body': 'הקישור וההגדרות שלך נמצאים בעמוד "הקישור שלי".',
    'access.cta': 'מעבר לקישור שלי',
    company: 'חברה',
    companyOwn: 'החברה שלי',
    refresh: 'רענון',
    'col.name': 'שם',
    'col.role': 'תפקיד',
    'col.code': 'קוד',
    'col.plan': 'מסלול',
    'col.clients': 'לקוחות',
    'col.actions': 'פעולות',
    'role.employee': 'מאמן/ת',
    'role.company_manager': 'מנהל/ת',
    'role.sys_admin': 'מנהל מערכת',
    placeholder: 'משתמש זמני — לא חשבון אמיתי',
    noCode: 'אין קוד עדיין',
    planCompany: 'ברירת המחדל של החברה',
    edit: 'עריכה',
    copyLink: 'העתקת קישור',
    empty: 'אין עדיין מאמנים בחברה. הזמינו את הראשון.',
    noCompany: 'לחשבון הזה אין חברה. בחרו חברה למעלה.',
    loadTeamError: 'לא הצלחנו לטעון את הצוות.',
    'invite.open': 'הזמנת מאמן',
    'invite.title': 'הזמנת מאמן',
    'invite.body': 'המאמן יקבל קישור הרשמה. אחרי ההרשמה הוא יופיע כאן עם קוד משלו.',
    'invite.email': 'אימייל של המאמן',
    'invite.send': 'יצירת הזמנה',
    'invite.creating': 'יוצר…',
    'invite.done': 'ההזמנה נוצרה. שלחו את קישור ההרשמה ל-{email}:',
    'invite.another': 'הזמנה נוספת',
    'invite.emailError': 'הזינו אימייל תקין.',
    'invite.error': 'יצירת ההזמנה נכשלה: {msg}',
    close: 'סגירה',
    'panel.title': 'עריכת {name}',
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function useEscape(active, onClose) {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, onClose]);
}

function InviteDialog({ open, onClose, companyId, t, direction }) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ busy: false, error: '', link: '', sentTo: '' });
  const closeRef = useRef(null);
  useEscape(open, onClose);
  useEffect(() => {
    if (open) {
      setEmail('');
      setState({ busy: false, error: '', link: '', sentTo: '' });
      setTimeout(() => closeRef.current?.focus(), 0);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) { setState((s) => ({ ...s, error: t('invite.emailError') })); return; }
    setState((s) => ({ ...s, busy: true, error: '' }));
    try {
      const invite = await RegistrationInvites.create({ email: value, role: 'employee', company_id: companyId || null });
      setState({ busy: false, error: '', link: staffSignupLink(window.location.origin, invite.code), sentTo: value });
    } catch (err) {
      setState((s) => ({ ...s, busy: false, error: t('invite.error', { msg: err.message || '' }) }));
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" dir={direction}>
          <motion.div className="absolute inset-0 bg-black/55" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={onClose} />
          <motion.div
            role="dialog" aria-modal="true" aria-labelledby="invite-title"
            className={`${card} relative w-full max-w-md p-5`}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="flex items-start justify-between gap-2">
              <h2 id="invite-title" className="text-[18px] font-semibold text-ink">{t('invite.title')}</h2>
              <button ref={closeRef} type="button" className={`${btnGhost} min-w-[44px] px-0 -m-2`} onClick={onClose} aria-label={t('close')}>
                <X size={18} aria-hidden />
              </button>
            </div>
            {state.link ? (
              <div className="mt-3 space-y-3">
                <p className="text-[13px] text-ink">{t('invite.done', { email: state.sentTo })}</p>
                <p dir="ltr" className="break-all rounded-md border border-line bg-canvas p-3 font-mono text-[13px] text-ink">{state.link}</p>
                <div className="flex flex-wrap gap-2">
                  <CopyButton text={state.link} label={t('share.copyLink')} copiedLabel={t('share.copied')} />
                  <button type="button" className={btnSecondary} onClick={() => { setEmail(''); setState({ busy: false, error: '', link: '', sentTo: '' }); }}>
                    {t('invite.another')}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="mt-3 space-y-3">
                <p className="text-[13px] text-ink-muted">{t('invite.body')}</p>
                <div>
                  <label htmlFor="invite-email" className={label}>{t('invite.email')}</label>
                  <input id="invite-email" type="email" dir="ltr" autoComplete="off" className={input} value={email}
                    onChange={(e) => { setEmail(e.target.value); setState((s) => ({ ...s, error: '' })); }} />
                </div>
                {state.error ? <p className={errorText} role="alert">{state.error}</p> : null}
                <div className="flex flex-wrap gap-2">
                  <button type="submit" className={btnPrimary} disabled={state.busy}>
                    {state.busy ? <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden /> : <UserPlus size={16} aria-hidden />}
                    {state.busy ? t('invite.creating') : t('invite.send')}
                  </button>
                  <button type="button" className={btnSecondary} onClick={onClose}>{t('close')}</button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}

function EditPanel({ member, onClose, onSaved, t, language, direction, companyName, canUpload }) {
  const closeRef = useRef(null);
  useEscape(!!member, onClose);
  useEffect(() => { if (member) setTimeout(() => closeRef.current?.focus(), 0); }, [member]);
  const profileId = member?.profile_id;
  const load = useCallback(() => DietitianSettings.get(profileId), [profileId]);
  const save = useCallback((patch) => DietitianSettings.save(profileId, patch), [profileId]);
  const isRtl = direction === 'rtl';

  return (
    <AnimatePresence>
      {member ? (
        <div className="fixed inset-0 z-[60]" dir={direction}>
          <motion.div className="absolute inset-0 bg-black/55" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={onClose} />
          <motion.aside
            role="dialog" aria-modal="true" aria-labelledby="edit-panel-title"
            className={`absolute inset-y-0 ${isRtl ? 'left-0 border-e' : 'right-0 border-s'} w-full max-w-xl overflow-y-auto border-line bg-surface`}
            initial={{ x: isRtl ? '-100%' : '100%', opacity: 0.6 }} animate={{ x: 0, opacity: 1 }} exit={{ x: isRtl ? '-100%' : '100%', opacity: 0.6 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="sticky top-0 z-20 flex items-center justify-between gap-2 border-b border-line bg-surface px-4 py-2">
              <h2 id="edit-panel-title" className="truncate text-[15px] font-semibold text-ink">
                {t('panel.title', { name: member.name || member.invite_code || '' })}
              </h2>
              <button ref={closeRef} type="button" className={`${btnGhost} min-w-[44px] px-0`} onClick={onClose} aria-label={t('close')}>
                <X size={18} aria-hidden />
              </button>
            </div>
            <div className="p-4">
              <DietitianSettingsForm
                key={profileId}
                load={load}
                save={save}
                t={t}
                language={language}
                companyName={companyName}
                canUpload={canUpload}
                layout="panel"
                idPrefix={`panel-${profileId}`}
                onSaved={onSaved}
              />
            </div>
          </motion.aside>
        </div>
      ) : null}
    </AnimatePresence>
  );
}

export default function TeamPage() {
  const { language, direction } = useLanguage();
  const t = makeT(strings, language);
  const { currentUser, loading: userLoading } = useCurrentUser();
  const role = currentUser?.role;
  const isAdmin = role === 'sys_admin';
  const allowed = isAdmin || role === 'company_manager';

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [presets, setPresets] = useState([]);
  const [team, setTeam] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadTeam = useCallback(async () => {
    setStatus('loading');
    setError('');
    try {
      setTeam(await Team.get(companyId || undefined));
      setStatus('ready');
    } catch (err) {
      setError(err.message || '');
      setStatus('error');
    }
  }, [companyId]);

  useEffect(() => { if (allowed) loadTeam(); }, [allowed, loadTeam]);

  useEffect(() => {
    if (!allowed) return;
    PricingPresets.list({ all: isAdmin }).then((list) => setPresets(Array.isArray(list) ? list : [])).catch(() => setPresets([]));
    if (isAdmin) Companies.list().then((list) => setCompanies(Array.isArray(list) ? list : [])).catch(() => setCompanies([]));
  }, [allowed, isAdmin]);

  const presetById = useMemo(() => new Map(presets.map((p) => [p.id, p])), [presets]);
  const planLabel = (id) => (id ? presetName(presetById.get(id), language) || id : t('planCompany'));
  const members = team?.members || [];
  const company = team?.company || null;
  const closePanel = useCallback(() => setEditing(null), []);
  const closeInvite = useCallback(() => setInviteOpen(false), []);

  if (userLoading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center text-[13px] text-ink-muted" role="status" dir={direction}>
        <Loader2 size={18} className="animate-spin me-2 motion-reduce:animate-none" aria-hidden /> {t('loading')}
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-4" dir={direction}>
        <div className={`${card} w-full max-w-md p-6 text-center`}>
          <ShieldAlert size={24} className="mx-auto text-ink-muted" aria-hidden />
          <h1 className="mt-2 text-[18px] font-semibold text-ink">{t('access.title')}</h1>
          <p className="mt-1 text-[13px] text-ink-muted">{t('access.body')}</p>
          <Link to="/my-link" className={`${btnPrimary} mt-4`}>{t('access.cta')}</Link>
        </div>
      </div>
    );
  }

  const roleLabel = (r) => t(`role.${r}`);

  const nameCell = (m) => (
    <div className="min-w-0">
      <p className="truncate text-[13px] font-medium text-ink">{m.name || '—'}</p>
      {m.is_placeholder ? (
        <span className="mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium bg-[var(--progress-faint)] text-progress">
          {t('placeholder')}
        </span>
      ) : null}
    </div>
  );

  const codeCell = (m) => (m.invite_code
    ? <span className="font-mono text-[13px] font-semibold text-ink" dir="ltr">{m.invite_code}</span>
    : <span className="text-[13px] text-ink-muted">{t('noCode')}</span>);

  const actions = (m) => (
    <div className="flex flex-wrap justify-end gap-2">
      <CopyButton text={m.client_link} label={t('copyLink')} copiedLabel={t('share.copied')} ariaLabel={`${t('copyLink')}: ${m.name || ''}`} />
      <button type="button" className={btnSecondary} onClick={() => setEditing(m)} aria-label={`${t('edit')}: ${m.name || ''}`}>
        <Pencil size={16} aria-hidden />
        {t('edit')}
      </button>
    </div>
  );

  const inviteCompanyId = companyId || company?.id || null;

  return (
    <div className="min-h-screen bg-canvas pb-16" dir={direction}>
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <header className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className={pageTitle}>{t('page.title')}{company?.name ? <span className="text-ink-muted"> · {company.name}</span> : null}</h1>
            <p className="mt-2 max-w-2xl text-[13px] text-ink-muted">{t('page.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            {isAdmin ? (
              <div className="min-w-[200px]">
                <label htmlFor="team-company" className={label}>{t('company')}</label>
                <select id="team-company" className={input} value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                  <option value="">{t('companyOwn')}</option>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            ) : null}
            <button type="button" className={btnSecondary} onClick={loadTeam} disabled={status === 'loading'}>
              <RefreshCw size={16} className={status === 'loading' ? 'animate-spin motion-reduce:animate-none' : ''} aria-hidden />
              {t('refresh')}
            </button>
            <button type="button" className={btnPrimary} onClick={() => setInviteOpen(true)} disabled={!inviteCompanyId}>
              <UserPlus size={16} aria-hidden />
              {t('invite.open')}
            </button>
          </div>
        </header>

        {status === 'loading' && !team ? (
          <div className="flex items-center justify-center gap-2 py-16 text-[13px] text-ink-muted" role="status">
            <Loader2 size={18} className="animate-spin motion-reduce:animate-none" aria-hidden /> {t('loading')}
          </div>
        ) : status === 'error' ? (
          <div className={`${card} p-6 text-center`} role="alert">
            <AlertCircle size={24} className="mx-auto text-[color:var(--danger-text)]" aria-hidden />
            <p className="mt-2 text-[13px] text-ink">{t('loadTeamError')}</p>
            {error ? <p className={`${hint} mt-1`}>{error}</p> : null}
            <button type="button" className={`${btnPrimary} mt-4`} onClick={loadTeam}>
              <RefreshCw size={16} aria-hidden /> {t('retry')}
            </button>
          </div>
        ) : !company ? (
          <div className={`${card} p-6 text-center text-[13px] text-ink-muted`}>{t('noCompany')}</div>
        ) : members.length === 0 ? (
          <div className={`${card} p-6 text-center`}>
            <p className="text-[13px] text-ink-muted">{t('empty')}</p>
            <button type="button" className={`${btnPrimary} mt-4`} onClick={() => setInviteOpen(true)}>
              <UserPlus size={16} aria-hidden /> {t('invite.open')}
            </button>
          </div>
        ) : (
          <>
            {/* Mobile: one card per coach */}
            <ul className="space-y-3 md:hidden">
              {members.map((m) => (
                <li key={m.profile_id} className={`${card} p-4`}>
                  <div className="flex items-start justify-between gap-3">
                    {nameCell(m)}
                    <span className="shrink-0 text-[11px] text-ink-muted">{roleLabel(m.role)}</span>
                  </div>
                  <dl className="mt-3 grid grid-cols-3 gap-2 text-[13px]">
                    <div><dt className={hint}>{t('col.code')}</dt><dd>{codeCell(m)}</dd></div>
                    <div><dt className={hint}>{t('col.plan')}</dt><dd className="text-ink truncate">{planLabel(m.pricing_preset_id)}</dd></div>
                    <div><dt className={hint}>{t('col.clients')}</dt><dd className="text-ink tabular-nums">{m.client_count ?? 0}</dd></div>
                  </dl>
                  <div className="mt-3">{actions(m)}</div>
                </li>
              ))}
            </ul>

            {/* Desktop table */}
            <div className={`${card} hidden md:block overflow-x-auto`}>
              <table className="w-full border-collapse text-start">
                <thead className="sticky top-0 bg-surface">
                  <tr className="border-b border-line">
                    {['name', 'role', 'code', 'plan', 'clients'].map((col) => (
                      <th key={col} scope="col" className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-ink-muted text-start ${col === 'clients' ? 'text-end' : ''}`}>
                        {t(`col.${col}`)}
                      </th>
                    ))}
                    <th scope="col" className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-ink-muted text-end">{t('col.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {members.map((m) => (
                    <tr key={m.profile_id} className="hover:bg-moss/40 transition-colors duration-120">
                      <td className="px-4 py-3 max-w-[260px]">{nameCell(m)}</td>
                      <td className="px-4 py-3 text-[13px] text-ink">{roleLabel(m.role)}</td>
                      <td className="px-4 py-3">{codeCell(m)}</td>
                      <td className="px-4 py-3 text-[13px] text-ink">{planLabel(m.pricing_preset_id)}</td>
                      <td className="px-4 py-3 text-[13px] text-ink tabular-nums text-end">{m.client_count ?? 0}</td>
                      <td className="px-4 py-3">{actions(m)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      <EditPanel
        member={editing}
        onClose={closePanel}
        onSaved={() => loadTeam()}
        t={t}
        language={language}
        direction={direction}
        companyName={company?.name || ''}
        canUpload
      />
      <InviteDialog open={inviteOpen} onClose={closeInvite} companyId={inviteCompanyId} t={t} direction={direction} />
    </div>
  );
}
